import { useEffect, useMemo, useRef, useState } from 'react'
import './ReactIslands.css'
import './TestModeApp.css'
import type { CrucialReactProps } from './crucialReactMount'
import GeneralErrorButton from './GeneralErrorButton'
import QuestionCard from './QuestionCard'
import type { QuestionData } from './questionTypes'
import type { ThemeMode } from './themeHandler'
import { shuffleArray, shuffleDifferent } from './questionUtils'

type Objective = {
  id: number
  title: string
}

export type TestModeConfig = CrucialReactProps & {
  testId: string
  subject: {
    id: number
    examCode: string
    friendlyName: string
    friendlyNameShort: string
    imageUrl?: string | null
    subtitle?: string | null
  }
  title: string
  subTitle?: string | null
  questions: QuestionData[]
  objectives: Objective[]
  includedObjectives: number[]
  filteredObjectives: boolean
  questionCount: number
  totalQuestionCount: number
  totalSeconds: number
  totalTimeHumanized: string
  secondsPerQuestion: number
  testTimerEnabled: boolean
  showChangeTestOptions: boolean
  demoMode: boolean
  authenticated: boolean
  userHasAiEntitlement: boolean
  demoResultsCaptureEnabled: boolean
  demoAttemptId?: string | null
  demoSignupCompleted?: boolean
  demoLoginUrl?: string | null
  firstTouchCampaign?: string | null
  showAutoTestWarning: boolean
  showTest: boolean
  warnOnExit: boolean
  autoScorePassing: boolean
  canReport: boolean
  canVote: boolean
  testEndImageUrl?: string | null
  lottieGradeUrl: string
  lottieGradePlaybackSpeed?: number | null
  gradeAnimationDurationMs: number
  xpPerCorrectQuestion?: number
  testHistoryUrl?: string | null
  shareUrl?: string | null
  shareOrgName?: string | null
  demoPlansUrl?: string | null
  plausibleProps: {
    subject: string
    authenticated: string
    demoMode: string
    approximateQuestionCount: string
  }
}

type GradeResult = {
  correct: boolean
  answered: boolean
  selectedIds: number[]
}

type MatchingResponsePair = {
  prompt: string
  selectedAnswer: string
}

type ScoreSummary = {
  score: number
  earnedXp: number
  correctCount: number
  incorrectCount: number
  correctByObj: Record<number, number>
  incorrectByObj: Record<number, number>
  resultsByQuestion: Record<number, GradeResult>
}

type TestLifecycleStatus = 'loaded' | 'graded'
type DemoAiGateCta = 'question_chat' | 'follow_up_prompt' | 'ai_chat_tab'

type FinalizedResponse = {
  questionId: number
  selectedIds: number[]
  wasCorrect: boolean
  wasFlaggedByUser: boolean
  setId: string | null
  matchingResponses: MatchingResponsePair[] | null
}

type StoredDemoAttempt = {
  version: 1
  testId: string
  subjectId: number
  questionIds: number[]
  selectedAnswersByQuestion: Record<number, number[]>
  matchingSelectionsByQuestion: Record<number, string[]>
  textAnswersByQuestion: Record<number, string>
  markedForReview: Record<number, boolean>
  scoreSummary: ScoreSummary
  responses: FinalizedResponse[]
  createdAt: number
}

type StoredTestLifecycle = {
  testId: string
  status: TestLifecycleStatus
  updatedAt: number
}

const testLeaveWarning = 'Refreshing or leaving this page will start a new test. You can review completed tests from Test History.'
const testLifecycleStoragePrefix = 'ce-test-mode-lifecycle'
const demoAttemptStoragePrefix = 'ce-demo-results-attempt'
const demoResultsCtaVariant = 'score-personalized-v1'

const getScoreBucket = (score: number) => {
  if (score < 50) return 'under-50'
  if (score < 80) return '50-79'
  return '80-plus'
}

const trackPlausible = (eventName: string, props: Record<string, string>) => {
  if (typeof window.plausible !== 'function') return

  try {
    window.plausible(eventName, { props })
  } catch (error) {
    console.error(error)
  }
}

const trackDemoAnalytics = (
  eventName: string,
  examCode: string,
  campaign: string | null | undefined,
  props: Record<string, string> = {},
) => {
  const sharedProps = {
    ...props,
    'exam-code': examCode,
    ...(campaign ? { 'utm-campaign': campaign } : {}),
  }
  trackPlausible(eventName, sharedProps)

  try {
    window.gtag?.('event', eventName, {
      ...props,
      exam_code: examCode,
      ...(campaign ? { utm_campaign: campaign } : {}),
    })
  } catch (error) {
    console.error(error)
  }

  try {
    void window.fetch?.('/api/analytics/demo-results', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ eventName, examCode }),
      keepalive: true,
    }).catch((error) => console.error(error))
  } catch (error) {
    console.error(error)
  }
}

const getDemoAttemptStorageKey = (testId: string) => `${demoAttemptStoragePrefix}:${testId}`

const writeDemoAttempt = (attempt: StoredDemoAttempt) => {
  try {
    window.localStorage.setItem(getDemoAttemptStorageKey(attempt.testId), JSON.stringify(attempt))
  } catch (error) {
    console.error(error)
  }
}

const readDemoAttempt = (testId: string): StoredDemoAttempt | null => {
  try {
    const raw = window.localStorage.getItem(getDemoAttemptStorageKey(testId))
    if (!raw) return null
    const attempt = JSON.parse(raw) as StoredDemoAttempt
    if (attempt.version !== 1 || attempt.testId !== testId || !Array.isArray(attempt.questionIds)) return null
    return attempt
  } catch (error) {
    console.error(error)
    return null
  }
}

const removeDemoAttempt = (testId: string) => {
  try {
    window.localStorage.removeItem(getDemoAttemptStorageKey(testId))
  } catch (error) {
    console.error(error)
  }
}

const wasPageRefreshed = () => {
  const navEntries = performance.getEntriesByType('navigation')
  if (navEntries.length === 0) return false
  return (navEntries[0] as PerformanceNavigationTiming).type === 'reload'
}

const getTestLifecycleStorageKey = (subjectId: number) => `${testLifecycleStoragePrefix}:${subjectId}`

const readTestLifecycle = (subjectId: number): StoredTestLifecycle | null => {
  try {
    const raw = window.localStorage.getItem(getTestLifecycleStorageKey(subjectId))
    if (!raw) return null
    const lifecycle = JSON.parse(raw) as Partial<StoredTestLifecycle>
    if (lifecycle.status !== 'loaded' && lifecycle.status !== 'graded') return null
    if (typeof lifecycle.testId !== 'string' || typeof lifecycle.updatedAt !== 'number') return null
    return {
      testId: lifecycle.testId,
      status: lifecycle.status,
      updatedAt: lifecycle.updatedAt,
    }
  } catch (error) {
    console.error(error)
    return null
  }
}

const writeTestLifecycle = (subjectId: number, testId: string, status: TestLifecycleStatus) => {
  try {
    const lifecycle: StoredTestLifecycle = {
      testId,
      status,
      updatedAt: Date.now(),
    }
    window.localStorage.setItem(getTestLifecycleStorageKey(subjectId), JSON.stringify(lifecycle))
  } catch (error) {
    console.error(error)
  }
}

const shouldShowRefreshRecovery = (subjectId: number, testHistoryUrl?: string | null) => {
  if (!testHistoryUrl || !wasPageRefreshed()) return false
  return readTestLifecycle(subjectId)?.status === 'graded'
}

const formatTime = (totalSeconds: number) => {
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = Math.max(0, totalSeconds % 60)
  const pad = (value: number) => value.toString().padStart(2, '0')
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
}

const showConfigForm = () => {
  const wrapper = document.querySelector('.show-test, .show-form')
  if (!wrapper) return
  wrapper.classList.remove('show-test')
  wrapper.classList.add('show-form')
}

const requestNextFrame = (callback: FrameRequestCallback) => {
  if (typeof window.requestAnimationFrame === 'function') {
    return window.requestAnimationFrame(callback)
  }

  return window.setTimeout(() => callback(performance.now()), 0)
}

const cancelNextFrame = (handle: number) => {
  if (typeof window.cancelAnimationFrame === 'function') {
    window.cancelAnimationFrame(handle)
    return
  }

  window.clearTimeout(handle)
}

export default function TestModeApp({
  testId,
  subject,
  title,
  subTitle,
  questions,
  objectives,
  includedObjectives,
  filteredObjectives,
  questionCount,
  totalQuestionCount,
  totalSeconds,
  totalTimeHumanized,
  secondsPerQuestion,
  testTimerEnabled,
  showChangeTestOptions,
  demoMode,
  authenticated,
  userHasAiEntitlement,
  demoResultsCaptureEnabled,
  demoAttemptId,
  demoSignupCompleted = false,
  demoLoginUrl,
  firstTouchCampaign,
  showAutoTestWarning,
  warnOnExit,
  autoScorePassing,
  canReport,
  canVote,
  testEndImageUrl,
  lottieGradeUrl,
  lottieGradePlaybackSpeed,
  gradeAnimationDurationMs,
  xpPerCorrectQuestion = 0,
  testHistoryUrl,
  shareUrl,
  shareOrgName,
  demoPlansUrl,
  plausibleProps,
  theme,
}: TestModeConfig & { theme: ThemeMode }) {
  const [hasStarted, setHasStarted] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const [showQuestionList, setShowQuestionList] = useState(false)
  const [selectedAnswersByQuestion, setSelectedAnswersByQuestion] = useState<Record<number, number[]>>(() => {
    const initialSelections: Record<number, number[]> = {}
    questions.forEach((q) => {
      if (q.kind !== 'Ordered') return
      const ids = q.answers.map((answer) => answer.id)
      initialSelections[q.id] = shuffleArray(ids)
    })
    return initialSelections
  })
  const [matchingSelectionsByQuestion, setMatchingSelectionsByQuestion] = useState<Record<number, string[]>>(() => {
    const initialSelections: Record<number, string[]> = {}
    questions.forEach((q) => {
      if (q.kind !== 'Matching') return
      const count = q.matching?.items.length ?? 0
      initialSelections[q.id] = Array.from({ length: count }, () => '')
    })
    return initialSelections
  })
  const [textAnswersByQuestion, setTextAnswersByQuestion] = useState<Record<number, string>>({})
  const [markedForReview, setMarkedForReview] = useState<Record<number, boolean>>({})
  const [isGraded, setIsGraded] = useState(false)
  const [scoreSummary, setScoreSummary] = useState<ScoreSummary | null>(null)
  const [scoreProgressPercent, setScoreProgressPercent] = useState(0)
  const [filter, setFilter] = useState<'all' | 'correct' | 'incorrect' | 'marked'>('all')
  const [timeRemaining, setTimeRemaining] = useState(totalSeconds)
  const [shareModalOpen, setShareModalOpen] = useState(false)
  const [demoAiLoginPrompt, setDemoAiLoginPrompt] = useState<DemoAiGateCta | null>(null)
  const [demoAttemptSaveStatus, setDemoAttemptSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [showRefreshNotice, setShowRefreshNotice] = useState(() =>
    shouldShowRefreshRecovery(subject.id, testHistoryUrl),
  )
  const timerRef = useRef<number | null>(null)
  const gradeTimeoutRef = useRef<number | null>(null)
  const gradingInFlightRef = useRef(false)
  const suppressUnloadWarningRef = useRef(false)
  const demoResultsCtaViewTrackedRef = useRef(false)
  const demoResultsViewedTrackedRef = useRef(false)
  const demoSignupCompletedTrackedRef = useRef(false)
  const demoAttemptRestoredRef = useRef(false)
  const testEndImageRef = useRef<HTMLImageElement | null>(null)
  const mobileQuestionNavQuery = '(max-width: 991.98px)'
  const explanationsLocked = demoResultsCaptureEnabled && demoMode && !authenticated

  const demoAuthHref = useMemo(() => {
    if (!demoLoginUrl) return null

    const returnUrl = new URL(window.location.href)
    returnUrl.searchParams.set('DemoMode', 'true')
    returnUrl.searchParams.set('ShowTest', 'true')
    returnUrl.searchParams.set('DemoAttemptId', testId)
    returnUrl.searchParams.delete('DemoQuestionIds')
    questions.forEach((question) => returnUrl.searchParams.append('DemoQuestionIds', question.id.toString()))
    returnUrl.searchParams.delete('DemoSignupCompleted')

    const loginUrl = new URL(demoLoginUrl, window.location.origin)
    loginUrl.searchParams.set('returnUrl', `${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`)
    return `${loginUrl.pathname}${loginUrl.search}${loginUrl.hash}`
  }, [demoLoginUrl, questions, testId])

  const questionNumberById = useMemo(() => {
    const map = new Map<number, number>()
    questions.forEach((q, index) => {
      map.set(q.id, index + 1)
    })
    return map
  }, [questions])

  const totalPages = questionCount + 1
  const currentQuestion = currentPage > 0 && currentPage <= questionCount ? questions[currentPage - 1] : null
  const shouldWarnOnExit = warnOnExit && (hasStarted || isGraded)
  const weakestObjective = useMemo(() => {
    if (!scoreSummary) return null

    return objectives
      .map((objective) => {
        const correct = scoreSummary.correctByObj[objective.id] ?? 0
        const incorrect = scoreSummary.incorrectByObj[objective.id] ?? 0
        const total = correct + incorrect
        return {
          ...objective,
          percent: total === 0 ? 100 : (correct / total) * 100,
          total,
        }
      })
      .filter((objective) => objective.total > 0)
      .sort((a, b) => a.percent - b.percent)[0] ?? null
  }, [objectives, scoreSummary])

  useEffect(() => {
    if (!isGraded || !scoreSummary) {
      setScoreProgressPercent(0)
      return
    }

    setScoreProgressPercent(0)
    let secondFrameHandle: number | null = null
    const firstFrameHandle = requestNextFrame(() => {
      secondFrameHandle = requestNextFrame(() => {
        setScoreProgressPercent(Math.round(scoreSummary.score))
      })
    })

    return () => {
      cancelNextFrame(firstFrameHandle)
      if (secondFrameHandle !== null) {
        cancelNextFrame(secondFrameHandle)
      }
    }
  }, [isGraded, scoreSummary])

  useEffect(() => {
    if (
      !demoMode ||
      !demoPlansUrl ||
      !isGraded ||
      !scoreSummary ||
      demoResultsCtaViewTrackedRef.current
    ) return

    demoResultsCtaViewTrackedRef.current = true
    trackPlausible('demo-results-cta-view', {
      subject: plausibleProps.subject,
      'score-bucket': getScoreBucket(scoreSummary.score),
      'weakest-objective': weakestObjective?.id.toString() ?? 'none',
      'cta-variant': demoResultsCtaVariant,
      placement: 'score-summary',
    })
  }, [demoMode, demoPlansUrl, isGraded, plausibleProps.subject, scoreSummary, weakestObjective])

  useEffect(() => {
    if (
      !demoMode ||
      !isGraded ||
      !scoreSummary ||
      (authenticated && demoAttemptId) ||
      demoResultsViewedTrackedRef.current
    ) return

    demoResultsViewedTrackedRef.current = true
    trackDemoAnalytics('demo_results_viewed', subject.examCode, firstTouchCampaign, {
      subject: plausibleProps.subject,
      'score-bucket': getScoreBucket(scoreSummary.score),
    })
  }, [authenticated, demoAttemptId, demoMode, firstTouchCampaign, isGraded, plausibleProps.subject, scoreSummary, subject.examCode])

  useEffect(() => {
    if (!demoMode || !authenticated || !demoSignupCompleted || demoSignupCompletedTrackedRef.current) return

    demoSignupCompletedTrackedRef.current = true
    trackDemoAnalytics('demo_signup_completed', subject.examCode, firstTouchCampaign, {
      subject: plausibleProps.subject,
    })

    const cleanUrl = new URL(window.location.href)
    cleanUrl.searchParams.delete('DemoSignupCompleted')
    window.history.replaceState(window.history.state, '', `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`)
  }, [authenticated, demoMode, demoSignupCompleted, firstTouchCampaign, plausibleProps.subject, subject.examCode])

  useEffect(() => {
    if (!authenticated || !demoAttemptId || demoAttemptRestoredRef.current) return
    demoAttemptRestoredRef.current = true

    const attempt = readDemoAttempt(demoAttemptId)
    const currentQuestionIds = new Set(questions.map((question) => question.id))
    if (
      !attempt ||
      attempt.subjectId !== subject.id ||
      attempt.questionIds.length !== questions.length ||
      attempt.questionIds.some((id) => !currentQuestionIds.has(id))
    ) {
      setDemoAttemptSaveStatus('error')
      return
    }

    setSelectedAnswersByQuestion(attempt.selectedAnswersByQuestion)
    setMatchingSelectionsByQuestion(attempt.matchingSelectionsByQuestion)
    setTextAnswersByQuestion(attempt.textAnswersByQuestion)
    setMarkedForReview(attempt.markedForReview)
    setScoreSummary(attempt.scoreSummary)
    setHasStarted(true)
    setCurrentPage(questionCount + 1)
    setIsGraded(true)
    setDemoAttemptSaveStatus('saving')
    writeTestLifecycle(subject.id, attempt.testId, 'graded')

    const submission = window.sendFinalizedTestSubmission?.(subject.id, attempt.testId, attempt.responses, true)
    if (!submission || typeof (submission as PromiseLike<unknown>).then !== 'function') {
      setDemoAttemptSaveStatus('error')
      return
    }

    void Promise.resolve(submission)
      .then(() => {
        removeDemoAttempt(attempt.testId)
        setDemoAttemptSaveStatus('saved')
      })
      .catch((error) => {
        console.error(error)
        setDemoAttemptSaveStatus('error')
      })
  }, [authenticated, demoAttemptId, questionCount, questions, subject.id])

  const normalizeMatchValue = (value: string) => value.trim().toLowerCase()
  const buildMatchingResponses = (question: QuestionData, selections: string[]): MatchingResponsePair[] =>
    (question.matching?.items ?? []).map((item, index) => ({
      prompt: item.prompt,
      selectedAnswer: selections[index] ?? '',
    }))

  const getMatchingOptions = (question: QuestionData) => {
    const seen = new Set<string>()
    const options: string[] = []
    question.matching?.items.forEach((item) => {
      const candidate = item.answer?.trim()
      if (!candidate) return
      const normalized = candidate.toLowerCase()
      if (seen.has(normalized)) return
      seen.add(normalized)
      options.push(candidate)
    })
    return options
  }

  const applyForcePassing = () => {
    const forcedSelections: Record<number, number[]> = { ...selectedAnswersByQuestion }
    const forcedTextAnswers: Record<number, string> = { ...textAnswersByQuestion }
    const forcedMatchingSelections: Record<number, string[]> = { ...matchingSelectionsByQuestion }

    questions.forEach((q) => {
      const makeCorrect = Math.random() < 0.9
      if (q.kind === 'TextInput') {
        if (makeCorrect) {
          forcedTextAnswers[q.id] = q.textInput?.correctAnswers?.[0] ?? ''
        } else {
          forcedTextAnswers[q.id] = 'Incorrect'
        }
        return
      }

      if (q.kind === 'Ordered') {
        const orderedIds = q.answers.map((answer) => answer.id)
        forcedSelections[q.id] = makeCorrect ? orderedIds : shuffleDifferent(orderedIds)
        return
      }

      if (q.kind === 'Matching') {
        const items = q.matching?.items ?? []
        const options = getMatchingOptions(q)
        forcedMatchingSelections[q.id] = items.map((item) => {
          if (makeCorrect) return item.answer
          const incorrect = options.find((option) => normalizeMatchValue(option) !== normalizeMatchValue(item.answer))
          return incorrect ?? item.answer
        })
        return
      }

      const correctIds = q.answers.filter((answer) => answer.isCorrect).map((answer) => answer.id)
      const incorrectIds = q.answers.filter((answer) => !answer.isCorrect).map((answer) => answer.id)
      forcedSelections[q.id] = makeCorrect ? correctIds : incorrectIds
    })

    setSelectedAnswersByQuestion(forcedSelections)
    setTextAnswersByQuestion(forcedTextAnswers)
    setMatchingSelectionsByQuestion(forcedMatchingSelections)

    return { forcedSelections, forcedTextAnswers, forcedMatchingSelections }
  }

  const gradeChoiceQuestion = (question: QuestionData, selectedIds: number[]): GradeResult => {
    let allCorrect = true
    let madeSelection = false

    question.answers.forEach((answer) => {
      const isSelected = selectedIds.includes(answer.id)
      if (isSelected) {
        madeSelection = true
        allCorrect = allCorrect && answer.isCorrect
      } else if (answer.isCorrect) {
        allCorrect = false
      }
    })

    return {
      correct: allCorrect,
      answered: madeSelection,
      selectedIds,
    }
  }

  const gradeTextInputQuestion = (question: QuestionData, inputValue: string): GradeResult => {
    const userInput = inputValue.trim()
    const correctAnswers = question.textInput?.correctAnswers ?? []
    const isCorrect = correctAnswers.some((answer) => answer.trim().toUpperCase() === userInput.toUpperCase())

    return {
      correct: isCorrect,
      answered: userInput.length > 0,
      selectedIds: question.textInput?.answerId ? [question.textInput.answerId] : [],
    }
  }

  const gradeOrderedQuestion = (question: QuestionData, selectedIds: number[]): GradeResult => {
    const correctOrder = question.answers.map((answer) => answer.id)
    const rawSelected = selectedIds.length > 0 ? selectedIds : correctOrder
    const uniqueSelected = Array.from(new Set(rawSelected.filter((id) => correctOrder.includes(id))))
    const missing = correctOrder.filter((id) => !uniqueSelected.includes(id))
    const finalizedIds = [...uniqueSelected, ...missing]
    const isCorrect =
      finalizedIds.length === correctOrder.length && finalizedIds.every((id, index) => id === correctOrder[index])

    return {
      correct: isCorrect,
      answered: finalizedIds.length > 0,
      selectedIds: finalizedIds,
    }
  }

  const gradeMatchingQuestion = (question: QuestionData, selections: string[]): GradeResult => {
    const items = question.matching?.items ?? []
    const normalizedSelections = selections.map((value) => normalizeMatchValue(value))
    const answered = normalizedSelections.some((value) => value.length > 0)
    const correct =
      items.length > 0 &&
      items.every((item, index) => {
        const selected = normalizedSelections[index] ?? ''
        return selected.length > 0 && selected === normalizeMatchValue(item.answer)
      })

    return {
      correct,
      answered,
      selectedIds: [],
    }
  }

  const gradeAllQuestions = (
    selections: Record<number, number[]>,
    textAnswers: Record<number, string>,
    matchingSelections: Record<number, string[]>,
  ) => {
    let correctCount = 0
    let incorrectCount = 0
    const correctByObj: Record<number, number> = {}
    const incorrectByObj: Record<number, number> = {}
    const resultsByQuestion: Record<number, GradeResult> = {}

    questions.forEach((q) => {
      const selectedIds = selections[q.id] ?? []
      const inputValue = textAnswers[q.id] ?? ''
      const matchSelections = matchingSelections[q.id] ?? []
      const result =
        q.kind === 'TextInput'
          ? gradeTextInputQuestion(q, inputValue)
          : q.kind === 'Matching'
            ? gradeMatchingQuestion(q, matchSelections)
          : q.kind === 'Ordered'
            ? gradeOrderedQuestion(q, selectedIds)
            : gradeChoiceQuestion(q, selectedIds)
      resultsByQuestion[q.id] = result

      if (result.correct) {
        correctCount += 1
      } else {
        incorrectCount += 1
      }

      if (correctByObj[q.objectiveId] === undefined) {
        correctByObj[q.objectiveId] = 0
        incorrectByObj[q.objectiveId] = 0
      }

      if (result.correct) {
        correctByObj[q.objectiveId] += 1
      } else {
        incorrectByObj[q.objectiveId] += 1
      }
    })

    const score = correctCount + incorrectCount > 0 ? (correctCount / (correctCount + incorrectCount)) * 100 : 0
    const earnedXp = correctCount * xpPerCorrectQuestion

    return {
      score,
      earnedXp,
      correctCount,
      incorrectCount,
      correctByObj,
      incorrectByObj,
      resultsByQuestion,
    }
  }

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
  }

  const clearGradeTimeout = () => {
    if (gradeTimeoutRef.current !== null) {
      window.clearTimeout(gradeTimeoutRef.current)
      gradeTimeoutRef.current = null
    }
  }

  const grade = () => {
    if (isGraded) return
    gradingInFlightRef.current = true
    clearGradeTimeout()

    clearTimer()

    let selections = selectedAnswersByQuestion
    let textAnswers = textAnswersByQuestion
    let matchingSelections = matchingSelectionsByQuestion

    if (autoScorePassing) {
      const forced = applyForcePassing()
      selections = forced.forcedSelections
      textAnswers = forced.forcedTextAnswers
      matchingSelections = forced.forcedMatchingSelections
    }

    const summary = gradeAllQuestions(selections, textAnswers, matchingSelections)
    setScoreSummary(summary)
    setIsGraded(true)
    writeTestLifecycle(subject.id, testId, 'graded')
    window.updateWeeklyXp?.(summary.earnedXp)

    if (summary.score >= 80) {
      window.launchConfetti?.()
    }

    const responses: FinalizedResponse[] = questions
      .filter((q) => !q.skipSaveResponse)
      .map((q) => {
        const result = summary.resultsByQuestion[q.id]
        const matchingResponses =
          q.kind === 'Matching'
            ? buildMatchingResponses(q, matchingSelections[q.id] ?? [])
            : null
        return {
          questionId: q.id,
          selectedIds: result.selectedIds,
          wasCorrect: result.correct,
          wasFlaggedByUser: markedForReview[q.id] === true,
          setId: q.setId ?? null,
          matchingResponses,
        }
      })

    if (explanationsLocked) {
      writeDemoAttempt({
        version: 1,
        testId,
        subjectId: subject.id,
        questionIds: questions.map((question) => question.id),
        selectedAnswersByQuestion: selections,
        matchingSelectionsByQuestion: matchingSelections,
        textAnswersByQuestion: textAnswers,
        markedForReview,
        scoreSummary: summary,
        responses,
        createdAt: Date.now(),
      })
    } else {
      void window.sendFinalizedTestSubmission?.(subject.id, testId, responses)
    }

    if (typeof window.plausible === 'function') {
      window.setTimeout(() => {
        try {
          window.plausible?.('test-scored', {
            props: {
              subject: plausibleProps.subject,
              authenticated: plausibleProps.authenticated,
              'demo-mode': plausibleProps.demoMode,
              'approximate-question-count': plausibleProps.approximateQuestionCount,
            },
          })
        } catch (error) {
          console.error(error)
        }
      }, 1500)
    }
  }

  const gradeWithAnimation = () => {
    if (gradingInFlightRef.current) return
    gradingInFlightRef.current = true
    clearTimer()
    try {
      window.showLottieLoadingModal?.({
        lottieUrl: lottieGradeUrl,
        speed: lottieGradePlaybackSpeed ?? undefined,
        message: 'Submitting your exam...',
        backdropOpacity: 0.9,
      })
    } catch (error) {
      console.error(error)
    }

    clearGradeTimeout()
    gradeTimeoutRef.current = window.setTimeout(() => {
      gradeTimeoutRef.current = null
      grade()
      window.hideLoadingModal?.()
    }, gradeAnimationDurationMs)
  }

  const startTest = () => {
    if (hasStarted) return

    setHasStarted(true)
    setCurrentPage(1)
    setShowQuestionList(false)
    window.trackStartTime?.(questionCount)

    if (testTimerEnabled) {
      setTimeRemaining(totalSeconds)
    }
  }

  const incrementPage = (diff: number) => {
    const nextPage = currentPage + diff
    if (nextPage < 0 || nextPage > totalPages) return
    setShowQuestionList(false)
    setCurrentPage(nextPage)
  }

  const navigateTo = (page: number) => {
    if (page < 0 || page > totalPages) return
    setShowQuestionList(false)
    setCurrentPage(page)
  }

  const toggleQuestionList = () => {
    setShowQuestionList((prev) => !prev)
  }

  const toggleReviewMark = () => {
    if (currentPage < 1 || currentPage > questionCount) return
    const question = questions[currentPage - 1]
    const isMarked = markedForReview[question.id] === true
    const nextMarked = !isMarked

    setMarkedForReview((prev) => ({
      ...prev,
      [question.id]: nextMarked,
    }))

    if (nextMarked) {
      window.enqueueToast?.({
        theme: 'success',
        title: 'Marked',
        message: `Question ${currentPage} was marked for later review`,
        duration: 2000,
      })
    } else {
      window.enqueueToast?.({
        theme: 'danger',
        title: 'Removed',
        message: `Question ${currentPage} was removed from review`,
        duration: 2000,
      })
    }
  }

  useEffect(() => {
    if (authenticated && demoAttemptId) return
    writeTestLifecycle(subject.id, testId, 'loaded')
  }, [authenticated, demoAttemptId, subject.id, testId])

  useEffect(() => {
    if (!testEndImageUrl) {
      testEndImageRef.current = null
      return
    }

    const image = new Image()
    image.decoding = 'async'
    image.src = testEndImageUrl
    testEndImageRef.current = image
    image.decode?.().catch(() => {
      // The normal image load path still works if decode is interrupted or unsupported.
    })

    return () => {
      if (testEndImageRef.current === image) {
        testEndImageRef.current = null
      }
    }
  }, [testEndImageUrl])

  useEffect(() => {
    if (!shouldWarnOnExit) return

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (suppressUnloadWarningRef.current) return
      event.preventDefault()
      event.returnValue = testLeaveWarning
      return testLeaveWarning
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [shouldWarnOnExit])

  useEffect(() => {
    if (!shouldWarnOnExit) return

    const confirmExit = () => suppressUnloadWarningRef.current || window.confirm(testLeaveWarning)

    const handleClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      if (!(event.target instanceof Element)) return

      const link = event.target.closest<HTMLAnchorElement>('a[href]')
      if (!link) return
      if (link.dataset.testExitWithoutWarning === 'true') return
      if (link.target && link.target.toLowerCase() !== '_self') return

      const rawHref = link.getAttribute('href')?.trim()
      if (!rawHref || rawHref.startsWith('#')) return
      if (/^(javascript|mailto|tel):/i.test(rawHref)) return

      const url = new URL(link.href, window.location.href)
      const current = new URL(window.location.href)
      const onlyChangesHash =
        url.origin === current.origin &&
        url.pathname === current.pathname &&
        url.search === current.search &&
        url.hash !== current.hash
      if (onlyChangesHash) return

      if (confirmExit()) return

      event.preventDefault()
      event.stopPropagation()
    }

    const handleSubmit = (event: SubmitEvent) => {
      if (event.defaultPrevented) return
      if (confirmExit()) return

      event.preventDefault()
      event.stopPropagation()
    }

    document.addEventListener('click', handleClick, true)
    document.addEventListener('submit', handleSubmit, true)
    return () => {
      document.removeEventListener('click', handleClick, true)
      document.removeEventListener('submit', handleSubmit, true)
    }
  }, [shouldWarnOnExit])

  useEffect(() => {
    if (!hasStarted || isGraded || !testTimerEnabled) {
      clearTimer()
      return
    }

    timerRef.current = window.setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearTimer()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => {
      clearTimer()
    }
  }, [hasStarted, isGraded, testTimerEnabled])

  useEffect(() => {
    if (!hasStarted || isGraded) return
    if (timeRemaining > 0) return

    gradeWithAnimation()
  }, [timeRemaining, hasStarted, isGraded])

  useEffect(() => {
    if (!hasStarted || isGraded) return
    if (currentPage <= 1) return
    if (window.scrollY > 15) {
      document.getElementById('testContent')?.scrollIntoView()
    }
  }, [currentPage, hasStarted, isGraded])

  useEffect(() => {
    document.querySelectorAll<HTMLElement>('.blur-on-question-change').forEach((el) => {
      if (typeof el.blur === 'function') {
        el.blur()
      }
    })
  }, [currentPage])

  useEffect(() => {
    if (!shareModalOpen && !showQuestionList && !demoAiLoginPrompt) return
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (shareModalOpen) {
          setShareModalOpen(false)
        }
        if (showQuestionList) {
          setShowQuestionList(false)
        }
        if (demoAiLoginPrompt) {
          setDemoAiLoginPrompt(null)
        }
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [demoAiLoginPrompt, shareModalOpen, showQuestionList])

  useEffect(() => {
    if (!showQuestionList || !window.matchMedia(mobileQuestionNavQuery).matches) return

    const { body } = document
    const previousOverflow = body.style.overflow
    body.style.overflow = 'hidden'

    return () => {
      body.style.overflow = previousOverflow
    }
  }, [showQuestionList, mobileQuestionNavQuery])

  useEffect(() => {
    const launcher = document.querySelector<HTMLElement>('.ai-hover-show')
    if (!launcher) return
    launcher.style.display = isGraded ? 'flex' : 'none'
  }, [isGraded])

  useEffect(() => {
    return () => {
      clearTimer()
      clearGradeTimeout()
    }
  }, [])

  const renderedQuestions = useMemo(() => {
    if (!scoreSummary) return []
    return questions.filter((q) => {
      const result = scoreSummary.resultsByQuestion[q.id]
      if (!result) return false
      if (filter === 'correct') return result.correct
      if (filter === 'incorrect') return !result.correct
      if (filter === 'marked') return markedForReview[q.id]
      return true
    })
  }, [filter, markedForReview, questions, scoreSummary])

  const getObjectiveScore = (objId: number) => {
    if (!scoreSummary) return 'N/A'
    const correct = scoreSummary.correctByObj[objId] ?? 0
    const incorrect = scoreSummary.incorrectByObj[objId] ?? 0
    const total = correct + incorrect
    if (total === 0) return 'N/A (0/0)'
    return `${Math.round((correct / total) * 100)}% (${correct}/${total})`
  }

  const trackDemoAccountClick = (
    cta: 'create_account' | 'sign_in' | DemoAiGateCta,
    placement: 'save-card' | 'explanation-gate',
    willNavigate = true,
  ) => {
    if (willNavigate) suppressUnloadWarningRef.current = true
    const eventName = placement === 'explanation-gate'
      ? 'demo_explanation_gate_clicked'
      : 'demo_save_results_clicked'
    trackDemoAnalytics(eventName, subject.examCode, firstTouchCampaign, {
      subject: plausibleProps.subject,
      cta,
      placement,
    })
  }

  const openDemoAiLoginPrompt = (cta: DemoAiGateCta) => {
    trackDemoAccountClick(cta, 'explanation-gate', false)
    setDemoAiLoginPrompt(cta)
  }

  const trackDemoResultsCtaClick = () => {
    if (!scoreSummary) return

    suppressUnloadWarningRef.current = true
    trackPlausible('demo-results-cta-click', {
      subject: plausibleProps.subject,
      'score-bucket': getScoreBucket(scoreSummary.score),
      'weakest-objective': weakestObjective?.id.toString() ?? 'none',
      'cta-variant': demoResultsCtaVariant,
      placement: 'score-summary',
      cta: 'unlock',
    })
  }

  const showTimerWrapper = testTimerEnabled || !hasStarted
  const timerDisplay = testTimerEnabled ? formatTime(timeRemaining) : formatTime(totalSeconds)

  return (
    <div className="tm-app cer-shell" data-theme={theme}>
      <div
        className={`tm-content${!isGraded && hasStarted && currentPage === questionCount + 1 ? ' tm-content--end' : ''}`}
        id="testContent"
      >
        <div className="tm-header">
          <div className="tm-header-row">
            <div className="tm-header-left">
              {showTimerWrapper && (
                <div id="remainingTimeWrapper" className="tm-timer" title="Time remaining in your test">
                  <span id="remainingTime" data-timer-seconds={timeRemaining}>
                    {testTimerEnabled && timeRemaining <= 0 ? 'Times up!' : timerDisplay}
                  </span>
                </div>
              )}
              {hasStarted && !isGraded && (
                <div className="tm-header-actions">
                  <cer-btn
                    onClick={toggleReviewMark}
                    className="blur-on-question-change"
                    data-style="ghost"
                    title="Mark for Review"
                    type="button"
                    role="button"
                    tabIndex={0}
                  >
                    <i className="fa-solid fa-flag"></i>
                    <span className="cer-only-lg">Mark for Review</span>
                  </cer-btn>
                  <cer-btn
                    onClick={toggleQuestionList}
                    className="blur-on-question-change"
                    data-style="ghost"
                    title="Toggle question navigation"
                    type="button"
                    role="button"
                    tabIndex={0}
                  >
                    <i className="fa-solid fa-list"></i>
                    <span className="cer-only-lg">Toggle Navigation</span>
                  </cer-btn>
                </div>
              )}
            </div>
            <div>
              <GeneralErrorButton canReport={canReport} />
            </div>
          </div>
        </div>

        {showRefreshNotice && testHistoryUrl && !hasStarted && !isGraded && (
          <div className="tm-recovery" role="status">
            <div>
              <strong>Refresh detected.</strong>{' '}
              <a
                href={testHistoryUrl}
                onClick={() => {
                  suppressUnloadWarningRef.current = true
                }}
              >
                Click here to view your test history for this subject
                <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
              </a>
            </div>
            <button
              type="button"
              className="tm-recovery-dismiss"
              aria-label="Dismiss refresh recovery message"
              onClick={() => {
                setShowRefreshNotice(false)
              }}
            >
              <i className="fa-solid fa-xmark" aria-hidden="true"></i>
            </button>
          </div>
        )}

        {!isGraded && currentPage === 0 && (
          <div className="tm-page" data-question-page="0">
            <div className="tm-intro-stack">
              <div className="tm-card">
                <div className="tm-intro-header">
                  {subject.imageUrl ? (
                    <img className="tm-logo" src={subject.imageUrl} alt={`${subject.friendlyName} Logo`} />
                  ) : null}
                  <h3>{title}</h3>
                  {subTitle ? <div className="tm-subtitle">{subTitle}</div> : null}
                </div>
                <ul className="tm-meta">
                  <li className="cer-bold">{questionCount} Questions</li>
                  <li>
                    {testTimerEnabled ? (
                      <>
                        <span className="cer-bold">{totalTimeHumanized}</span>
                        <span className="cer-text-muted"> ({secondsPerQuestion} seconds per question)</span>
                      </>
                    ) : (
                      <span>Unlimited time</span>
                    )}
                  </li>
                  <li>
                    {!showAutoTestWarning && (
                      <div className="tm-objectives">
                        {objectives.map((objective) => {
                          if (filteredObjectives && !includedObjectives.includes(objective.id)) {
                            return null
                          }
                          return (
                            <cer-badge key={objective.id} data-variant="secondary">
                              {objective.title}
                            </cer-badge>
                          )
                        })}
                      </div>
                    )}
                  </li>
                  <li>
                    <div className="tm-actions">
                      {showChangeTestOptions && (
                        <cer-btn
                          data-style="ghost"
                          data-block="true"
                          onClick={showConfigForm}
                          type="button"
                          role="button"
                          tabIndex={0}
                        >
                          <i className="fa fa-solid fa-gear fa-fw"></i>
                          Change Options
                        </cer-btn>
                      )}
                      <cer-btn
                        data-variant="success"
                        data-block="true"
                        onClick={startTest}
                        type="button"
                        role="button"
                        tabIndex={0}
                      >
                        Launch Test
                      </cer-btn>
                    </div>
                  </li>
                </ul>
              </div>
              {demoMode && (
                <div className="tm-demo">
                  <div className="tm-demo-body">
                    <h3>Free Preview</h3>
                    <p>
                      This test is a free preview, no account required.
                      <br />
                      Subscribe to unlock all content, keep track of your scores, and access AI features!
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {!isGraded && hasStarted && currentQuestion && (
          <div
            className="tm-question"
            data-question-page={currentPage}
            data-question-reviewable="true"
            data-question-marked-for-review={markedForReview[currentQuestion.id] ? 'true' : 'false'}
          >
            <div className="tm-question-header">
              <span>
                Question {currentPage} of {questionCount}
              </span>
              {markedForReview[currentQuestion.id] ? (
                <cer-badge data-variant="warning" className="tm-marked" title={`Question ${currentPage} was marked for later review`}>
                  <i className="fa-solid fa-flag"></i>
                  Marked
                </cer-badge>
              ) : null}
            </div>
            <QuestionCard
              question={currentQuestion}
              mode="test"
              selectedAnswerIds={selectedAnswersByQuestion[currentQuestion.id] ?? []}
              textAnswer={textAnswersByQuestion[currentQuestion.id] ?? ''}
              matchingSelections={matchingSelectionsByQuestion[currentQuestion.id] ?? []}
              onSelectAnswer={(answerId) => {
                setSelectedAnswersByQuestion((prev) => {
                  const existing = prev[currentQuestion.id] ?? []
                  const nextSelections =
                    currentQuestion.kind === 'MultipleChoice'
                      ? existing.includes(answerId)
                        ? existing.filter((id) => id !== answerId)
                        : [...existing, answerId]
                      : [answerId]
                  return { ...prev, [currentQuestion.id]: nextSelections }
                })
              }}
              onOrderChange={(nextIds) => {
                setSelectedAnswersByQuestion((prev) => ({
                  ...prev,
                  [currentQuestion.id]: nextIds,
                }))
              }}
              onTextAnswerChange={(value) => {
                setTextAnswersByQuestion((prev) => ({ ...prev, [currentQuestion.id]: value }))
              }}
              onMatchingChange={(index, value) => {
                setMatchingSelectionsByQuestion((prev) => {
                  const existing = prev[currentQuestion.id] ?? []
                  const next = [...existing]
                  next[index] = value
                  return { ...prev, [currentQuestion.id]: next }
                })
              }}
              isGraded={false}
              gradeResult={null}
              showAnswerButton={false}
              canVote={canVote}
              showAiChatLink={false}
            />
          </div>
        )}

        {!isGraded && hasStarted && currentPage === questionCount + 1 && (
          <div className="tm-end" data-question-page={questionCount + 1}>
            {testEndImageUrl ? <img className="tm-end-image" src={testEndImageUrl} alt="" loading="eager" /> : null}
            <p>
              Looks like that's it. You can go back and review your answers or click the button below to grade your test.
            </p>
            <div>
              <cer-btn data-variant="danger" onClick={gradeWithAnimation} type="button" role="button" tabIndex={0}>
                Submit for grading
              </cer-btn>
            </div>
          </div>
        )}

        {hasStarted && !isGraded && (
          <div className="tm-pagination" id="paginationWrapper">
            <div className="tm-pagination-inner">
              <div className="tm-pagination-actions">
                <cer-btn
                  type="button"
                  className="blur-on-question-change"
                  data-style="ghost"
                  onClick={() => incrementPage(-1)}
                  aria-label="Back"
                  role="button"
                  tabIndex={0}
                >
                  <i className="fa-solid fa-arrow-left fa-fw"></i>
                  <span className="tm-button-text">Back</span>
                </cer-btn>
                <cer-btn
                  type="button"
                  className="blur-on-question-change"
                  data-style="ghost"
                  title="Show question navigation"
                  onClick={toggleQuestionList}
                  aria-label="Questions"
                  role="button"
                  tabIndex={0}
                >
                  <i className="fa-solid fa-list"></i>
                  <span className="tm-button-text">Questions</span>
                </cer-btn>
                <cer-btn
                  type="button"
                  className="blur-on-question-change"
                  data-style="ghost"
                  onClick={() => incrementPage(1)}
                  aria-label="Next"
                  role="button"
                  tabIndex={0}
                >
                  <span className="tm-button-text">Next</span>
                  <i className="fa-solid fa-arrow-right fa-fw"></i>
                </cer-btn>
              </div>
              {showQuestionList && (
                <div className="tm-page-grid-shell" role="dialog" aria-modal="true" aria-labelledby="question-navigation-title">
                  <div className="tm-page-grid-header">
                    <div className="tm-page-grid-title" id="question-navigation-title">
                      Question navigation
                    </div>
                    <cer-btn
                      type="button"
                      data-style="ghost"
                      className="tm-page-grid-close"
                      onClick={() => setShowQuestionList(false)}
                      aria-label="Close question navigation"
                      role="button"
                      tabIndex={0}
                    >
                      <i className="fa-solid fa-xmark"></i>
                      <span className="tm-page-grid-close-text">Close</span>
                    </cer-btn>
                  </div>
                  <div className="tm-page-grid" id="paginationQuestionsWrapper">
                    {questions.map((q, index) => {
                      const pageNumber = index + 1
                      const isCurrent = pageNumber === currentPage
                      const isMarked = markedForReview[q.id]
                      return (
                        <cer-btn
                          type="button"
                          className={`tm-page-button blur-on-question-change${isCurrent ? ' is-current' : ''}`}
                          key={q.id}
                          onClick={() => navigateTo(pageNumber)}
                          role="button"
                          tabIndex={0}
                        >
                          {isMarked ? (
                            <>
                              <i className="fa-solid fa-flag fa-xs"></i> {pageNumber}
                            </>
                          ) : (
                            pageNumber
                          )}
                        </cer-btn>
                      )
                    })}
                    <cer-btn
                      type="button"
                      className="tm-page-button blur-on-question-change"
                      onClick={() => navigateTo(questionCount + 1)}
                      aria-label="Go to submit page"
                      role="button"
                      tabIndex={0}
                    >
                      <i className="fa-sharp-duotone fa-regular fa-rocket-launch"></i>
                    </cer-btn>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {isGraded && scoreSummary && (
          <>
            <div className="tm-score" id="scoreWrapper">
              {explanationsLocked && demoAuthHref && (
                <section className="cer-panel tm-card tm-save-card" aria-labelledby="demo-save-results-title">
                  <div className="tm-conversion-copy">
                    <cer-badge data-variant="success">Free account</cer-badge>
                    <h2 id="demo-save-results-title">Save your results and see your report card</h2>
                    <p>
                      Get a breakdown of your weak spots by domain and unlock every answer explanation. Free account,
                      takes 30 seconds.
                    </p>
                  </div>
                  <div className="tm-conversion-actions tm-save-actions">
                    <a
                      className="tm-conversion-link tm-conversion-link--primary"
                      href={demoAuthHref}
                      data-test-exit-without-warning="true"
                      onClick={() => trackDemoAccountClick('create_account', 'save-card')}
                    >
                      Create a free account
                      <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
                    </a>
                    <a
                      className="tm-sign-in-link"
                      href={demoAuthHref}
                      data-test-exit-without-warning="true"
                      onClick={() => trackDemoAccountClick('sign_in', 'save-card')}
                    >
                      Already have an account? Sign in
                    </a>
                  </div>
                </section>
              )}
              {authenticated && demoAttemptId && demoAttemptSaveStatus !== 'idle' && (
                <div
                  className={`tm-save-status tm-save-status--${demoAttemptSaveStatus}`}
                  role={demoAttemptSaveStatus === 'error' ? 'alert' : 'status'}
                >
                  {demoAttemptSaveStatus === 'saving' && 'Saving test results to your account...'}
                  {demoAttemptSaveStatus === 'saved' && 'Results saved to your account.'}
                  {demoAttemptSaveStatus === 'error' &&
                    'Your explanations are unlocked, but we could not save this demo attempt. Please try the test again.'}
                </div>
              )}
              <div className="cer-panel tm-card tm-score-header">
                <div className="tm-score-title">{subject.friendlyName}</div>
                <div className="tm-score-grid">
                  <div className="tm-score-row">
                    <span>Your Score:</span>
                    <span>{Math.round(scoreSummary.score)}%</span>
                  </div>
                  <div className="tm-score-row">
                    <span>XP Earned:</span>
                    <span>+{scoreSummary.earnedXp.toLocaleString()} xp</span>
                  </div>
                  {objectives.map((obj) => (
                    <div className="tm-score-row" key={obj.id}>
                      <span>{obj.title}:</span>
                      <span>{getObjectiveScore(obj.id)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div
                className="tm-score-progress cer-progress"
                role="progressbar"
                aria-valuenow={Math.round(scoreSummary.score)}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div className="cer-progress-bar" style={{ width: `${scoreProgressPercent}%` }}></div>
              </div>
              {demoMode && demoPlansUrl && (
                <section className="cer-panel tm-card tm-conversion-card" aria-labelledby="demo-results-cta-title">
                  <div className="tm-conversion-copy">
                    <cer-badge data-variant={scoreSummary.score >= 80 ? 'success' : 'info'}>
                      Your next step
                    </cer-badge>
                    <h2 id="demo-results-cta-title">
                      {scoreSummary.score >= 80
                        ? 'Strong start—now validate your exam readiness'
                        : 'Turn this score into a focused study plan'}
                    </h2>
                    <p>
                      {scoreSummary.score >= 80 ? (
                        <>
                          Unlock full access to all {totalQuestionCount.toLocaleString()} {subject.friendlyNameShort}{' '}
                          practice questions, full-length practice tests, score history, and AI study tools.
                        </>
                      ) : weakestObjective ? (
                        <>
                          Your biggest weakness is <strong>{weakestObjective.title}</strong>. Unlock full access to all{' '}
                          {totalQuestionCount.toLocaleString()} {subject.friendlyNameShort} practice questions to
                          strengthen it and track your progress.
                        </>
                      ) : (
                        <>
                          Unlock full access to all {totalQuestionCount.toLocaleString()} {subject.friendlyNameShort}{' '}
                          practice questions and track your progress.
                        </>
                      )}
                    </p>
                  </div>
                  <div className="tm-conversion-actions">
                    <a
                      className="tm-conversion-link tm-conversion-link--primary"
                      href={demoPlansUrl}
                      data-test-exit-without-warning="true"
                      onClick={trackDemoResultsCtaClick}
                    >
                      Unlock Full {subject.friendlyNameShort} Prep
                      <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
                    </a>
                  </div>
                </section>
              )}
              <div className="cer-panel tm-card">
                <div className="tm-filter-row">
                  <div>Filters</div>
                  <div className="tm-select">
                    <select
                      value={filter}
                      onChange={(event) => setFilter(event.target.value as typeof filter)}
                      className="cer-select cer-select--custom"
                    >
                      <option value="all">Show all</option>
                      <option value="incorrect">Show incorrect</option>
                      <option value="correct">Show correct</option>
                      <option value="marked">Show marked</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="tm-share-row">
                <div className="cer-text-muted">Scroll down to see your responses and detailed results</div>
                {shareUrl && (
                  <cer-btn
                    data-variant="secondary"
                    onClick={() => setShareModalOpen(true)}
                    type="button"
                    role="button"
                    tabIndex={0}
                  >
                    Share <i className="fa-solid fa-share-nodes"></i>
                  </cer-btn>
                )}
              </div>
            </div>

            {renderedQuestions.map((q, index) => {
              const result = scoreSummary.resultsByQuestion[q.id]
              const questionNumber = questionNumberById.get(q.id) ?? index + 1
              return (
                <div
                  key={q.id}
                  className={`tm-question${explanationsLocked ? ' tm-question--explanation-gated' : ''}`}
                  data-question-reviewable="true"
                  data-question-grade-result={result.correct.toString().toLowerCase()}
                  data-question-marked-for-review={markedForReview[q.id] ? 'true' : 'false'}
                >
                  <hr className="tm-divider" />
                  <div className="tm-question-header">
                    <span>Question {questionNumber}</span>
                    {markedForReview[q.id] ? (
                      <cer-badge data-variant="warning" className="tm-marked" title={`Question ${questionNumber} was marked for later review`}>
                        <i className="fa-solid fa-flag"></i>
                        Marked
                      </cer-badge>
                    ) : null}
                  </div>
                  <QuestionCard
                    question={q}
                    mode="test"
                    selectedAnswerIds={selectedAnswersByQuestion[q.id] ?? []}
                    textAnswer={textAnswersByQuestion[q.id] ?? ''}
                    matchingSelections={matchingSelectionsByQuestion[q.id] ?? []}
                    onSelectAnswer={() => null}
                    onOrderChange={() => null}
                    onTextAnswerChange={() => null}
                    onMatchingChange={() => null}
                    isGraded
                    gradeResult={result}
                    showAnswerButton={false}
                    canVote={canVote}
                    showAiChatLink={false}
                    aiChatButton={{
                      chatId: `testmode_${testId}`,
                      subjectId: subject.id,
                      questionId: q.id,
                    }}
                    aiChatLocked={authenticated && !userHasAiEntitlement}
                    explanationGate={explanationsLocked && demoAuthHref ? {
                      href: demoAuthHref,
                      onClick: (source) => {
                        if (source === 'question-chat') {
                          openDemoAiLoginPrompt('question_chat')
                        } else if (source === 'follow-up-prompt') {
                          openDemoAiLoginPrompt('follow_up_prompt')
                        } else {
                          trackDemoAccountClick('create_account', 'explanation-gate')
                        }
                      },
                    } : null}
                  />
                </div>
              )
            })}

            {shareUrl && shareModalOpen && (
              <div
                className="cer-modal-overlay"
                role="presentation"
                onClick={(event) => {
                  if (event.target === event.currentTarget) {
                    setShareModalOpen(false)
                  }
                }}
              >
                <div className="cer-modal" role="dialog" aria-modal="true" aria-labelledby="share-test-title">
                  <div className="cer-modal-header">
                    <h5 className="cer-modal-title" id="share-test-title">
                      Share Test Results
                    </h5>
                    <button type="button" className="cer-modal-close" aria-label="Close" onClick={() => setShareModalOpen(false)}>
                      <i className="fa-solid fa-xmark"></i>
                    </button>
                  </div>
                  <div className="cer-modal-body">
                    {shareOrgName ? (
                      <p>Use the link below to share these tests results with instructors and faculty at {shareOrgName}.</p>
                    ) : (
                      <p>Use the link below to share these tests results.</p>
                    )}
                    <div className="tm-share-input">
                      <input
                        id="shareTestInput"
                        className="cer-input"
                        type="text"
                        readOnly
                        value={shareUrl}
                        onClick={() => window.copyById?.('shareTestInput')}
                      />
                      <cer-btn
                        data-variant="secondary"
                        type="button"
                        onClick={() => window.copyById?.('shareTestInput')}
                        role="button"
                        tabIndex={0}
                      >
                        Copy
                      </cer-btn>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {isGraded && explanationsLocked && demoAuthHref ? (
        <a
          className="tm-ai-chat-gate"
          href={demoAuthHref}
          aria-label="AI Chat — free account required"
          data-test-exit-without-warning="true"
          onClick={(event) => {
            event.preventDefault()
            openDemoAiLoginPrompt('ai_chat_tab')
          }}
        >
          <i className="fa-duotone fa-solid fa-sparkles" aria-hidden="true"></i>
          <span>AI Chat</span>
          <i className="fa-solid fa-lock tm-ai-chat-gate-lock" aria-hidden="true"></i>
        </a>
      ) : null}
      {demoAiLoginPrompt && demoAuthHref ? (
        <div
          className="cer-modal-overlay tm-ai-login-modal"
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget) setDemoAiLoginPrompt(null)
          }}
        >
          <div className="cer-modal" role="dialog" aria-modal="true" aria-labelledby="demo-ai-login-title">
            <div className="cer-modal-header">
              <h5 className="cer-modal-title" id="demo-ai-login-title">Continue to sign in?</h5>
              <button
                type="button"
                className="cer-modal-close"
                aria-label="Close"
                onClick={() => setDemoAiLoginPrompt(null)}
              >
                <i className="fa-solid fa-xmark" aria-hidden="true"></i>
              </button>
            </div>
            <div className="cer-modal-body">
              <p>
                Bash and follow-up answers are available with a free account. We’ll return you to these results after
                you sign in or create one.
              </p>
            </div>
            <div className="cer-modal-footer">
              <button
                type="button"
                className="cer-button cer-button--ghost"
                onClick={() => setDemoAiLoginPrompt(null)}
              >
                Not now
              </button>
              <a
                className="cer-button cer-button--primary"
                href={demoAuthHref}
                data-test-exit-without-warning="true"
                onClick={() => {
                  suppressUnloadWarningRef.current = true
                  setDemoAiLoginPrompt(null)
                }}
              >
                Continue to sign in
              </a>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
